'use client';

import { useState, type JSX } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/lib/contexts/LanguageContext';

interface AddressInfoProps {
  formData: {
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    [key: string]: string | boolean;
  };
  onChange: (section: string, field: string, value: string | boolean) => void;
}

function getStringFieldValue(
  formData: AddressInfoProps['formData'],
  field: string,
): string | undefined {
  const value = formData[field];
  return typeof value === 'string' ? value : undefined;
}

export default function AddressInfo({
  formData,
  onChange,
}: Readonly<AddressInfoProps>): JSX.Element {
  const { t } = useLanguage();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});

  const validateField = (field: string, value: string): boolean => {
    let error = '';

    // First check if field is empty
    if (value.trim() === '') {
      error = t.fieldRequired;
    } else {
      // Field-specific validations
      switch (field) {
        case 'streetAddress':
          if (value.length < 5) {
            error = t.addressMinLength;
          }
          break;
        case 'city':
          if (!/^[A-Za-z\s-]+$/.test(value)) {
            error = t.invalidCity;
          }
          break;
        case 'postalCode':
          if (!/^[A-Za-z0-9\s-]+$/.test(value)) {
            error = t.invalidPostalCode;
          }
          break;
      }
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === '';
  };

  const markFieldAsDirty = (field: string): void => {
    setDirtyFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field: string): void => {
    markFieldAsDirty(field);
    const value = getStringFieldValue(formData, field);
    if (value !== undefined) {
      validateField(field, value);
    }
  };

  // Helper function to determine if we should show an error
  const shouldShowError = (field: string): boolean | undefined => {
    return dirtyFields[field] && Boolean(errors[field]);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">{t.companyAddress}</h3>
      <p className="text-sm text-gray-500">{t.provideAddress}</p>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="streetAddress" className="flex items-center">
            {t.streetAddress} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="streetAddress"
            placeholder={t.streetAddressPlaceholder}
            value={formData.streetAddress}
            onChange={(e) => onChange('address', 'streetAddress', e.target.value)}
            onBlur={() => handleBlur('streetAddress')}
            className={shouldShowError('streetAddress') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('streetAddress') && (
            <p className="text-sm text-red-500">{errors.streetAddress}</p>
          )}
          <p className="text-muted-foreground text-sm">{t.streetAddressHelp}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="city" className="flex items-center">
            {t.city} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="city"
            placeholder={t.cityPlaceholder}
            value={formData.city}
            onChange={(e) => onChange('address', 'city', e.target.value)}
            onBlur={() => handleBlur('city')}
            className={shouldShowError('city') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('city') && <p className="text-sm text-red-500">{errors.city}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="state" className="flex items-center">
            {t.stateRegion} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="state"
            placeholder={t.stateRegionPlaceholder}
            value={formData.state}
            onChange={(e) => onChange('address', 'state', e.target.value)}
            onBlur={() => handleBlur('state')}
            className={shouldShowError('state') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('state') && <p className="text-sm text-red-500">{errors.state}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="postalCode" className="flex items-center">
            {t.postalCode} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="postalCode"
            placeholder={t.postalCodePlaceholder}
            value={formData.postalCode}
            onChange={(e) => onChange('address', 'postalCode', e.target.value)}
            onBlur={() => handleBlur('postalCode')}
            className={shouldShowError('postalCode') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('postalCode') && (
            <p className="text-sm text-red-500">{errors.postalCode}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="country" className="flex items-center">
            {t.country} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Select
            value={formData.country}
            onValueChange={(value) => onChange('address', 'country', value)}
            required
          >
            <SelectTrigger
              id="country"
              className={shouldShowError('country') ? 'border-red-500' : ''}
              onBlur={() => handleBlur('country')}
            >
              <SelectValue placeholder={t.countryPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="us">{t.unitedStates}</SelectItem>
              <SelectItem value="ca">{t.canada}</SelectItem>
              <SelectItem value="uk">{t.unitedKingdom}</SelectItem>
              <SelectItem value="de">{t.germany}</SelectItem>
              <SelectItem value="fr">{t.france}</SelectItem>
              <SelectItem value="se">{t.sweden}</SelectItem>
              <SelectItem value="other">{t.other}</SelectItem>
            </SelectContent>
          </Select>
          {shouldShowError('country') && <p className="text-sm text-red-500">{errors.country}</p>}
        </div>
      </div>
    </div>
  );
}
